import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import TemplateDashboardService from '../../Services/TemplateDashboardService';

export default memo(CreateTemplateDashboardModal);

const initState = {
  error: '',
  isDefault: false,
  isPrivate: false,
  name: '',
  saveRequestPending: false,
};

function CreateTemplateDashboardModal(props) {
  const [state, updateState] = useState(initState);
  const source = CancelToken.source();
  const service = new TemplateDashboardService();

  const handleNameUpdate = ({ target: { name, value } }) => {
    updateState({ ...state, [name]: value });
  };

  const handleCheckboxClick = ({ target: { name } }) => {
    updateState({ ...state, [name]: !state[name] });
  };

  const handleSave = async () => {
    try {
      updateState({ ...state, saveRequestPending: true });

      const dashboard = await service.createTemplateDashboard(
        props.gtemplateID,
        { isPrivate: state.isPrivate, name: state.name, widgets: [] },
        source.token
      );

      if (state.isDefault) {
        await service.setUserDefaultDashboard(
          props.assetID,
          { dashboardID: dashboard._id },
          source.token
        );
      }

      props.handleDashboardCreated(dashboard);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, saveRequestPending: false });
    }
  };

  return (
    <Modal centered isOpen={props.isOpen} toggle={props.toggleModal}>
      <ModalHeader className="border-bottom-0" toggle={props.toggleModal}>
        Create New Dashboard
      </ModalHeader>
      <ModalBody className="container-fluid pt-0 pb-md-4">
        <div className="row">
          <p className="col-12 three-quarter-text text-left">
            Creating a new dashboard will allow for all assets associated to
            this Asset Model to view the same signal data on this dashboard
          </p>
        </div>
        <div className="row">
          <div className="col-12 form-group">
            <label className="nine-tenth-text" htmlFor="name">
              Dashboard Name
            </label>
            <input
              className="form-control form-control-sm rounded-lg"
              name="name"
              onChange={handleNameUpdate}
              type="text"
              value={state.name}
            />
          </div>
        </div>
        <div className="row my-2 my-md-1">
          <div className="col-12">
            <div className="form-check form-check-inline">
              <input
                checked={state.isDefault}
                className="form-check-input"
                name="isDefault"
                onChange={handleCheckboxClick}
                type="checkbox"
              />
              <label htmlFor="isDefault" className="form-check-label ml-3">
                Set as your Default Dashboard?
              </label>
            </div>
          </div>
        </div>
        <div className="row my-2 my-md-1">
          <div className="col-12">
            <div className="form-check form-check-inline">
              <input
                checked={state.isPrivate}
                className="form-check-input"
                name="isPrivate"
                onChange={handleCheckboxClick}
                type="checkbox"
              />
              <label htmlFor="isPrivate" className="form-check-label ml-3">
                Set as Private Dashboard and grant other users access?
              </label>
            </div>
          </div>
        </div>
        <div className={state.error ? 'row' : 'd-none'}>
          <p className="col-12 three-quarter-text text-danger">{state.error}</p>
        </div>
      </ModalBody>
      <ModalFooter className="border-top-0">
        <button
          className="btn btn-outline-secondary bg-white text-muted py-0 px-3 shadow-none three-quarter-text"
          onClick={props.toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit three-quarter-text py-0 px-3"
          disabled={!state.name || state.saveRequestPending}
          onClick={handleSave}
        >
          {state.saveRequestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Create'
          )}
        </button>
      </ModalFooter>
      <style jsx>{`
        .btn:focus {
          outline: none;
        }
      `}</style>
    </Modal>
  );
}

CreateTemplateDashboardModal.propTypes = {
  assetID: PropTypes.string,
  gtemplateID: PropTypes.string,
  handleDashboardCreated: PropTypes.func,
  isOpen: PropTypes.bool,
  orgID: PropTypes.string,
  toggleModal: PropTypes.func,
};
